import React from "react";
import { Citation } from "../../../../feature-components/MarkdownWithCitations/Citation.tsx";
import {
  CitationWidget as CitationWidgetType,
  WidgetContext,
  WidgetType,
} from "../../../../feature-components/WidgetLoader/WidgetContext";
import { useClaService } from "../../../../adapters/cla/state/hooks/useClaService.ts";
import { useMessageStatus } from "../../../../adapters/cla/state/hooks/useMessageStatus.ts";
import { ProductEventContext } from "../../../../adapters/product-events/ProductEventContext.ts";
import { ProductEvent } from "../../../../adapters/product-events/ProductEventTarget.ts";

export const CitationWidget: CitationWidgetType = (props) => {
  const claService = useClaService();
  const messageStatus = useMessageStatus(props.chatId, props.messageId);
  const [downloading, setDownloading] = React.useState(false);
  const context = React.useContext(WidgetContext);
  const productEventTarget = React.useContext(ProductEventContext);

  // Determine if this is a grouped citation
  const citationIds = props.ids || [props.id];

  // Get widget state from context (for grouped citations)
  const citationState = context?.widgetState?.[WidgetType.Citation] || {};

  // Build data array for all citations
  const citationDataWithIds = citationIds.map((citationId) => {
    const values = citationState[citationId] || {};
    const title = values.title;

    // Log error for missing data only after completion
    if (messageStatus === "completed" && !title) {
      productEventTarget?.dispatchEvent(
        new ProductEvent("exception", {
          description: `Citation data missing for ${citationId} after completion`,
          fatal: false,
        }),
      );
    }

    return {
      excerpt: values.excerpt || "",
      id: citationId,
      page: values.page?.toString() || "",
      pageNumber: values.page, // Keep original for sorting
      title: title || "Citation is not available now, please try again later.",
      sourceUrl: values.src,
    };
  });

  // Sort by page number: lower numbers first, null/undefined last
  const sortedData = citationDataWithIds.sort((a, b) => {
    const pageA = a.pageNumber;
    const pageB = b.pageNumber;

    // If both have page numbers, sort numerically
    if (pageA != null && pageB != null) {
      return pageA - pageB;
    }

    // If only one has a page number, it comes first
    if (pageA != null) return -1;
    if (pageB != null) return 1;

    // Both are null/undefined, maintain original order
    return 0;
  });

  // Deduplicate citations with identical page and excerpt
  // This handles cases where the backend returns multiple citation IDs
  // that point to the exact same content
  const uniqueCitations: typeof sortedData = [];
  const seen = new Set<string>();

  for (const citation of sortedData) {
    // Create a unique key from page and excerpt
    // (src is already the same within a group, so no need to include it)
    const key = JSON.stringify([citation.page, citation.excerpt]);

    if (!seen.has(key)) {
      seen.add(key);
      uniqueCitations.push(citation);
    }
  }

  // Extract deduplicated IDs and remove pageNumber field
  const dedupedCitationIds = uniqueCitations.map((c) => c.id);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const citationData = uniqueCitations.map(({ pageNumber, ...rest }) => rest);

  return (
    <Citation
      data={citationData}
      referenceIds={dedupedCitationIds}
      hideIds
      loading={messageStatus !== "completed"}
      onDownload={(resourceId) => {
        if (!props.chatId) return;
        setDownloading(true);
        claService
          .downloadCitationResource(props.chatId, resourceId)
          .finally(() => {
            setDownloading(false);
          });
      }}
      downloading={downloading}
    />
  );
};
